import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, ArrowRight, CheckCircle2, Quote,
  Bot, Users, Send, FileText, Workflow, BookOpen,
  BriefcaseBusiness, Share2, ShieldCheck,
  MessagesSquare, Undo2, BadgeDollarSign, Heart,
} from "lucide-react";
import { PublicNav } from "@/components/PublicNav";
import { Footer } from "@/components/Footer";
import { PublicSamWidget } from "@/components/PublicSamWidget";
import { Wordmark } from "@/components/Wordmark";
import { LandingDemoVideo } from "@/components/LandingDemoVideo";
import { Button } from "@/components/ui/button";

const FEATURES = [
  { name: "Knowledge Base",      icon: BookOpen,    desc: "Smart AI grounded in your business — your voice, your pricing, your truth." },
  { name: "AI Agents",           icon: Workflow,    desc: "Embed a grounded chatbot into your site in 30 seconds. It already knows what to say." },
  { name: "AI Chat Assistant",   icon: Bot,         desc: "Ask anything. Replies cite YOUR docs, not generic web copy." },
  { name: "Smart Leads",         icon: Users,       desc: "Organise contacts, see who matters, write better follow-ups." },
  { name: "Outreach Sequences",  icon: Send,        desc: "Send considered, multi-step emails that stop the moment someone replies." },
  { name: "Content Engine",      icon: FileText,    desc: "Notes in → on-brand emails, posts and blogs out — grounded in your KB by default." },
  { name: "Executive Assistant", icon: BriefcaseBusiness, desc: "A short daily briefing so you know what needs attention." },
  { name: "Multi-Channel",       icon: Share2,      desc: "Email, SMS, and chat from one place. (Coming soon.)" },
  { name: "Admin Controls",      icon: ShieldCheck, desc: "Calm, simple admin: who can see what, and a clear audit trail." },
];

const LOGOS = ["Northwind", "Halcyon", "Linear AI", "Atlas Co.", "Beacon", "Lumen", "Voyage"];

const HOW_IT_WORKS = [
  { n: "01", title: "Start the trial",      body: "Sign up in under a minute. No credit card. No long forms. We never share your details." },
  { n: "02", title: "Pick what to try",     body: "Choose the one thing you'd most like to make easier this week. Try it without changing anything else." },
  { n: "03", title: "Keep what helps",      body: "If it earns its keep, stay. If it doesn't, switch back to your old way instantly. No penalties." },
];

const PILLARS = [
  { icon: MessagesSquare, title: "Plain language",   body: "We explain everything clearly. No jargon, no buzzwords." },
  { icon: Undo2,          title: "No disruption",    body: "We improve what works. We don't replace what you've built." },
  { icon: Heart,          title: "Fully reversible", body: "Decide it's not for you? Switch back instantly. No penalties." },
  { icon: BadgeDollarSign,title: "One transparent price", body: "$29/month base. + $9 per additional seat. No upsells, no implementation fees, no surprises." },
];

const TESTIMONIALS = [
  {
    quote: "I was nervous to add another tool. Two weeks in, it actually saves me a couple of hours every day on follow-ups and notes. That's it. That's the magic.",
    name: "Mara Velez",
    role: "Owner, Velez Insurance Group",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80",
  },
  {
    quote: "Nobody on my team had to learn anything new. We kept our CRM, our inbox, everything. The AI just sits alongside and helps with the boring bits.",
    name: "Theo Kestrel",
    role: "Operations Lead, Halcyon Studio",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&q=80",
  },
  {
    quote: "Honestly, I thought 'AI' meant chaos. Symplee felt calm from day one. And the price is exactly what they said it would be.",
    name: "Priya Banerjee",
    role: "Founder, Linear Bookkeeping",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&q=80",
  },
];

const EASE = [0.22, 1, 0.36, 1]; // gentler than before

export default function Landing() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#FAF8F5] text-slate-900 dark:bg-[#0b1220] dark:text-white">
      <div className="aurora" />
      <div className="grain" />
      <PublicNav />

      {/* Hero */}
      <section className="relative">
        <div className="surface-glow pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-6xl px-6 pt-20 pb-28 sm:px-8 sm:pt-28 sm:pb-36 lg:px-12">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
            className="mx-auto flex max-w-3xl flex-col items-start text-left"
          >
            <motion.div
              initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.04, duration: 0.7, ease: EASE }}
              className="mb-8"
              data-testid="hero-wordmark"
            >
              <Wordmark variant="gradient" size="xl" />
            </motion.div>

            <motion.span
              initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.08 }}
              className="inline-flex items-center gap-2 rounded-full border border-slate-900/10 bg-white/70 px-3 py-1 text-xs text-slate-700 backdrop-blur-md dark:border-white/10 dark:bg-white/5 dark:text-white/70"
              data-testid="hero-eyebrow"
            >
              <Sparkles className="h-3.5 w-3.5 text-indigo-500" />
              <span className="font-medium">Grounded AI · Embeddable agents · For SMBs</span>
            </motion.span>

            <motion.h1
              initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.14, duration: 0.7, ease: EASE }}
              className="font-display mt-7 text-5xl font-light leading-[1.08] tracking-tight sm:text-6xl lg:text-7xl"
              data-testid="hero-headline"
            >
              AI made simple{" "}
              <br className="hidden sm:inline" />
              for your <span className="brand-text">business.</span>
            </motion.h1>

            <motion.p
              initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.22, duration: 0.7, ease: EASE }}
              className="mt-7 max-w-2xl text-lg leading-relaxed text-slate-600 dark:text-white/65"
              data-testid="hero-subheadline"
            >
              Smart AI that actually knows your business. Add it to your site in 30 seconds.
            </motion.p>

            <motion.div
              initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3, ease: EASE }}
              className="mt-9 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center"
            >
              <Link to="/signup" data-testid="hero-cta-primary">
                <Button className="group h-12 rounded-full bg-indigo-600 px-7 text-base text-white shadow-sm hover:bg-indigo-700 dark:bg-white dark:text-slate-900 dark:hover:bg-zinc-200">
                  Try free for 7 days
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </Link>
              <Link to="/pricing" data-testid="hero-cta-secondary">
                <Button variant="outline" className="h-12 rounded-full border-slate-900/15 bg-white/70 px-7 text-base text-slate-900 hover:bg-white dark:border-white/15 dark:bg-white/5 dark:text-white dark:hover:bg-white/10">
                  See pricing
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
              className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-600 dark:text-white/55"
              data-testid="hero-trust-pills"
            >
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> One simple price</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> No contracts</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> No upsells</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Cancel anytime</span>
            </motion.div>
          </motion.div>

          {/* Calm product mock */}
          <motion.div
            initial={{ y: 24, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
            className="relative mx-auto mt-20 max-w-5xl"
          >
            <div className="rounded-3xl border border-slate-900/8 bg-white/85 p-3 shadow-[0_24px_60px_rgba(15,23,42,0.08)] backdrop-blur-md dark:border-white/10 dark:bg-white/[0.03] sm:p-4">
              <div className="rounded-2xl border border-slate-900/5 bg-white p-6 dark:border-white/5 dark:bg-zinc-950 sm:p-10">
                <div className="grid gap-6 sm:grid-cols-3">
                  {[
                    { label: "Hours saved this week", value: "12.5", trend: "vs last week" },
                    { label: "Replies handled by AI", value: "84",   trend: "across email & chat" },
                    { label: "Tasks the assistant completed", value: "37", trend: "today" },
                  ].map((t) => (
                    <div key={t.label} className="rounded-xl border border-slate-900/5 bg-slate-50/70 p-5 dark:border-white/5 dark:bg-white/[0.03]">
                      <div className="text-eyebrow text-slate-500 dark:text-white/40">{t.label}</div>
                      <div className="font-display mt-2 text-3xl font-light text-slate-900 dark:text-white">{t.value}</div>
                      <div className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">{t.trend}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 rounded-xl border border-slate-900/5 bg-slate-50/70 p-5 dark:border-white/5 dark:bg-white/[0.02]">
                  <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-white/40">
                    <Sparkles className="h-3.5 w-3.5 text-indigo-500" /> Today, your assistant…
                  </div>
                  <div className="mt-3 space-y-2 text-sm text-slate-700 dark:text-white/75">
                    <div className="flex items-center justify-between"><span>Drafted a thank-you email to 6 new customers</span><span className="text-slate-400 dark:text-white/30">2m ago</span></div>
                    <div className="flex items-center justify-between"><span>Tidied your contact list — 12 duplicates removed</span><span className="text-slate-400 dark:text-white/30">14m ago</span></div>
                    <div className="flex items-center justify-between"><span>Answered 3 questions on your website chatbot</span><span className="text-slate-400 dark:text-white/30">1h ago</span></div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Iter 25 (D3) — "See it work" demo block. Stylized CSS-animated
          mockup of the Content Engine generating a grounded welcome
          email; replace with a real MP4 once recorded. */}
      <LandingDemoVideo />

      {/* Logo cloud */}
      <section className="relative border-y border-slate-900/5 bg-white/40 dark:border-white/5 dark:bg-white/[0.01]">
        <div className="mx-auto max-w-7xl px-6 py-10 sm:px-8 lg:px-12">
          <div className="text-center text-eyebrow text-slate-500 dark:text-white/40">Built for the businesses you've never heard a hype reel about</div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {LOGOS.map((l) => (
              <span key={l} className="logo-mark text-base sm:text-lg">{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Why Symplee — 4 pillars */}
      <section id="why" className="relative">
        <div className="mx-auto max-w-6xl px-6 py-28 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <div className="text-eyebrow text-indigo-600 dark:text-indigo-300">Why Symplee</div>
            <h2 className="font-display mt-3 text-4xl font-light leading-tight tracking-tight sm:text-5xl">
              AI should feel like <span className="brand-text">relief</span>, not overwhelming.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600 dark:text-white/65">
              We built Symplee for the business owners who looked at the AI hype, felt confused, and walked away.
              You don't need to rebuild your stack. You don't need a degree. You just need a little help with the busywork.
            </p>
          </div>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="pillars-grid">
            {PILLARS.map((p, idx) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: idx * 0.06, duration: 0.6, ease: EASE }}
                className="rounded-2xl border border-slate-900/8 bg-white p-6 shadow-sm dark:border-white/8 dark:bg-white/[0.03]"
                data-testid={`pillar-card-${idx}`}
              >
                <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300">
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-lg font-medium text-slate-900 dark:text-white">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-white/60">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Promise — plain, text-forward */}
      <section className="relative" data-testid="landing-promise-section">
        <div className="mx-auto max-w-3xl px-6 pb-20 sm:px-8 lg:px-12">
          <div className="text-center">
            <h2 className="font-display text-4xl font-light tracking-tight sm:text-5xl" data-testid="landing-promise-heading">
              Our Promise
            </h2>
            <p className="mt-3 text-base italic text-slate-600 dark:text-white/55" data-testid="landing-promise-subhead">
              Plain and honest. No fine print.
            </p>
          </div>

          <div className="mx-auto mt-10 max-w-2xl space-y-6 text-center text-lg leading-[1.75] text-slate-700 dark:text-white/75">
            <p>
              <strong className="font-medium text-slate-900 dark:text-white">No contracts</strong> &mdash; cancel anytime, no questions asked.
            </p>
            <p>
              <strong className="font-medium text-slate-900 dark:text-white">No late fees, no collections, no threats</strong> &mdash; short on cash one month? Just tell us, we&rsquo;ll pick things up next month.
            </p>
            <p>
              <strong className="font-medium text-slate-900 dark:text-white">No hidden charges</strong> &mdash; no implementation fees, no upgrade fees, no service fees.
            </p>
            <p>
              <strong className="font-medium text-slate-900 dark:text-white">Price locked for life</strong> &mdash; if rates go up, you stay grandfathered. No surprises, ever.
            </p>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/promise"
              className="text-sm text-indigo-600 underline-offset-4 hover:underline dark:text-indigo-300"
              data-testid="landing-promise-link"
            >
              Read our full promise &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* What we believe — quote callout */}
      <section className="relative">
        <div className="mx-auto max-w-4xl px-6 pb-28 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="relative rounded-3xl border border-slate-900/8 bg-gradient-to-br from-white via-white to-indigo-50/40 p-10 text-center shadow-sm dark:border-white/10 dark:from-white/[0.04] dark:via-white/[0.02] dark:to-indigo-500/[0.06] sm:p-14"
            data-testid="what-we-believe"
          >
            <div className="text-eyebrow text-indigo-600 dark:text-indigo-300">What we believe</div>
            <blockquote className="font-display mx-auto mt-5 max-w-2xl text-3xl font-light leading-[1.25] tracking-tight text-slate-900 dark:text-white sm:text-4xl">
              "AI should not feel overwhelming.<br />It should feel like relief."
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* Features / modules */}
      <section id="features" className="relative">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12">
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="max-w-xl">
              <div className="text-eyebrow text-indigo-600 dark:text-indigo-300">Everything you need, included</div>
              <h2 className="font-display mt-3 text-4xl font-light leading-tight tracking-tight sm:text-5xl">
                Nine clear modules<br />
                that do their <span className="brand-text">job quietly</span>.
              </h2>
            </div>
            <p className="max-w-md text-slate-600 dark:text-white/60">
              Every module is included on the single plan. No add-ons, no surprise upsells, no "ask sales for pricing".
            </p>
          </div>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, idx) => (
              <motion.div
                key={f.name}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: idx * 0.04, duration: 0.6, ease: EASE }}
                className="rounded-2xl border border-slate-900/8 bg-white p-6 shadow-sm transition hover:border-indigo-300/60 hover:shadow-md dark:border-white/8 dark:bg-white/[0.03] dark:hover:border-indigo-400/40"
                data-testid={`feature-card-${idx}`}
              >
                <div className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-indigo-600 dark:bg-white/5 dark:text-indigo-300">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-base font-medium text-slate-900 dark:text-white">{f.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-white/60">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="relative">
        <div className="mx-auto max-w-6xl px-6 py-24 sm:px-8 lg:px-12">
          <div className="text-eyebrow text-indigo-600 dark:text-indigo-300">How it works</div>
          <h2 className="font-display mt-3 max-w-2xl text-4xl font-light leading-tight tracking-tight sm:text-5xl">
            Three small steps.<br /> Nothing <span className="brand-text">scary</span>.
          </h2>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {HOW_IT_WORKS.map((s, idx) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08, duration: 0.7, ease: EASE }}
                className="relative rounded-2xl border border-slate-900/8 bg-white p-8 shadow-sm dark:border-white/8 dark:bg-white/[0.03]"
              >
                <div className="font-display text-5xl font-light text-indigo-200 dark:text-indigo-500/30">{s.n}</div>
                <h3 className="font-display mt-4 text-2xl font-medium text-slate-900 dark:text-white">{s.title}</h3>
                <p className="mt-3 leading-relaxed text-slate-600 dark:text-white/60">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="relative">
        <div className="mx-auto max-w-6xl px-6 py-24 sm:px-8 lg:px-12">
          <div className="text-eyebrow text-indigo-600 dark:text-indigo-300">Honest words from real owners</div>
          <h2 className="font-display mt-3 max-w-3xl text-4xl font-light tracking-tight sm:text-5xl">
            Small businesses, <span className="brand-text">calmer days</span>.
          </h2>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {TESTIMONIALS.map((t, idx) => (
              <motion.figure
                key={t.name}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08, duration: 0.7, ease: EASE }}
                className="relative rounded-2xl border border-slate-900/8 bg-white p-7 shadow-sm dark:border-white/8 dark:bg-white/[0.03]"
                data-testid={`testimonial-${idx}`}
              >
                <Quote className="absolute right-5 top-5 h-5 w-5 text-indigo-200 dark:text-white/20" />
                <blockquote className="font-display text-base leading-relaxed text-slate-800 dark:text-white/85">"{t.quote}"</blockquote>
                <figcaption className="mt-6 flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="h-9 w-9 rounded-full object-cover" />
                  <div>
                    <div className="text-sm text-slate-900 dark:text-white">{t.name}</div>
                    <div className="text-xs text-slate-500 dark:text-white/55">{t.role}</div>
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="relative">
        <div className="mx-auto max-w-5xl px-6 py-24 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7, ease: EASE }}
            className="relative overflow-hidden rounded-3xl border border-slate-900/8 bg-white p-10 shadow-sm dark:border-white/10 dark:bg-white/[0.03] sm:p-14"
          >
            <div className="absolute inset-x-0 top-0 h-1 brand-bg" />
            <div className="grid items-center gap-10 sm:grid-cols-5">
              <div className="sm:col-span-3">
                <div className="text-eyebrow text-indigo-600 dark:text-indigo-300">One simple price. Everything included.</div>
                <h3 className="font-display mt-3 text-4xl font-light tracking-tight text-slate-900 dark:text-white sm:text-5xl">
                  $29<span className="text-2xl text-slate-400 dark:text-white/40">/month</span>
                </h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-white/55" data-testid="pricing-teaser-seat-sub">for 1 seat (the owner)</p>
                <p className="mt-2 text-sm text-indigo-600/85 dark:text-indigo-300/85" data-testid="pricing-teaser-extra-seat">+ $9 / seat for additional teammates</p>
                <p className="mt-4 max-w-md text-slate-600 dark:text-white/60">
                  All nine modules. Unlimited use. A 7-day free trial. No credit card to start, no upsells, no surprises. Use code <span className="font-semibold text-indigo-600 dark:text-indigo-300">FOUNDER19</span> to lock in $19/mo for life (founders only).
                </p>
                <Link to="/signup" className="mt-7 inline-block" data-testid="pricing-teaser-cta">
                  <Button className="h-12 rounded-full bg-indigo-600 px-7 text-white hover:bg-indigo-700 dark:bg-white dark:text-slate-900 dark:hover:bg-zinc-200">
                    Start your 7-day trial <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
                <p className="mt-4 text-xs text-slate-500 dark:text-white/45">
                  No contracts · No commitments · Fully reversible
                </p>
              </div>
              <ul className="sm:col-span-2 space-y-3 text-sm text-slate-700 dark:text-white/70">
                {[
                  "Everything included, day one",
                  "No upsells, no implementation fees",
                  "+ $9/seat for additional teammates",
                  "Cancel anytime, fully reversible",
                  "Priority email support, real humans",
                ].map((l) => (
                  <li key={l} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 dark:text-emerald-400" /> {l}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <PublicSamWidget />
    </div>
  );
}
